import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "../axios";
import BackButton from "../components/BackButton";

const CommunityProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [scanContext, setScanContext] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState(null);

  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  })();
  const userId = storedUser?._id || storedUser?.id;

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;

    axios.get(`/community-products/${id}`)
      .then(res => { if (!cancelled) setProduct(res.data); })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.status === 404 ? "Produit introuvable." : "Erreur de chargement.");
      });

    if (userId) {
      axios.get(`/scans/history/${userId}`)
        .then(res => {
          if (cancelled) return;
          const scans = res.data?.scans || res.data || [];
          const match = scans.find(s => String(s.communityProductId) === id);
          if (match) setScanContext(match);
        })
        .catch(() => {});
    }

    return () => { cancelled = true; };
  }, [id, userId]);

  useEffect(() => {
    if (!product?.barcode || !userId) return;
    axios.get(`/push/by-user/${userId}`)
      .then(res => {
        const ids = res.data?.scannedProductIds || [];
        setSubscribed(ids.includes(String(product.barcode)));
      })
      .catch(() => {});
  }, [product, userId]);

  const handleToggleAlert = async () => {
    if (!userId) { toast.info("Connecte-toi pour activer les alertes."); return; }
    try {
      const newState = !subscribed;
      setSubscribed(newState);
      if (newState) {
        await axios.put("/push/subscribe-product", { userId, productId: product.barcode });
        toast.success("🔔 Alerte activée ! Tu seras notifié des mises à jour.");
      } else {
        await axios.post("/push/unsubscribe-product", { userId, productId: product.barcode });
        toast.info("🔕 Alerte désactivée.");
      }
    } catch {
      setSubscribed(s => !s);
      toast.error("❌ Impossible de changer l'abonnement.");
    }
  };

  const handleDelete = async () => {
    if (!scanContext) return;
    if (!window.confirm(`❗ Supprimer le scan de "${product?.name || "cet article"}" de tes crushs ?`)) return;
    try {
      await axios.delete(`/scans/${scanContext._id}`);
      toast.success("✅ Scan supprimé !");
      navigate("/client", { state: { tab: "scans" } });
    } catch {
      toast.error("❌ Erreur lors de la suppression.");
    }
  };

  const row = { fontSize: "14px", color: "#1e293b", marginBottom: "6px" };
  const grey = { color: "#94a3b8" };

  return (
    <div style={{
      minHeight: "100svh", background: "#f8fafc", padding: "16px 16px 40px",
      fontFamily: "system-ui, -apple-system, sans-serif", maxWidth: "480px", margin: "0 auto"
    }}>
      <BackButton />

      {error && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>📦</div>
          <p>{error}</p>
        </div>
      )}

      {!product && !error && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>Chargement…</div>
      )}

      {product && (
        <>
          {/* ── Bloc identité produit ── */}
          <div style={{
            background: "white", borderRadius: "16px", padding: "20px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.08)", marginBottom: "12px"
          }}>
            {product.photo ? (
              <img
                src={product.photo}
                alt={product.name}
                style={{ width: "100%", maxHeight: "180px", objectFit: "contain",
                         borderRadius: "8px", marginBottom: "16px", border: "1px solid #e2e8f0" }}
              />
            ) : (
              <div style={{ fontSize: "56px", textAlign: "center", marginBottom: "16px" }}>📦</div>
            )}
            <h1 style={{ fontSize: "19px", fontWeight: 700, color: "#1e293b", marginBottom: "6px", lineHeight: 1.3 }}>
              {product.name}
            </h1>
          </div>

          {/* ── Bloc contexte du scan ── */}
          <div style={{
            background: "white", borderRadius: "16px", padding: "16px 20px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)", marginBottom: "12px"
          }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase",
                        letterSpacing: "0.06em", marginBottom: "10px" }}>
              Contexte du scan
            </p>
            <div style={row}>🏪 {product.store || <span style={grey}>Non renseigné</span>}</div>
            <div style={row}>📍 {product.city || <span style={grey}>Non renseigné</span>}</div>
            <div style={row}>
              📅 Scanné le {new Date(product.createdAt).toLocaleDateString("fr-FR")}
            </div>
            <div style={row}>
              💰 {product.price != null ? `${product.price}€` : <span style={grey}>Non renseigné</span>}
            </div>
            <div style={row}>🏷️ {product.category || <span style={grey}>Non renseignée</span>}</div>
            <div style={row}>📦 {product.quantity || <span style={grey}>Non renseignée</span>}</div>
            <div style={{ ...row, marginBottom: 0, fontStyle: product.note ? "italic" : "normal" }}>
              💬 {product.note || <span style={{ ...grey, fontStyle: "normal" }}>Non renseignée</span>}
            </div>
          </div>

          {/* ── Bloc actions ── */}
          <div style={{
            background: "white", borderRadius: "16px", padding: "16px 20px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)", marginBottom: "12px",
            display: "flex", flexDirection: "column", gap: "10px"
          }}>
            <button
              onClick={handleToggleAlert}
              style={{
                width: "100%", minHeight: "44px", fontSize: "15px", fontWeight: 700,
                borderRadius: "22px", border: "none", cursor: "pointer",
                background: subscribed ? "#FEE2E2" : "#D1FAE5",
                color: subscribed ? "#991B1B" : "#065F46",
              }}
            >
              {subscribed ? "🔕 Stop alerte" : "🔔 Alerte"}
            </button>

            {scanContext && (
              <button
                onClick={handleDelete}
                style={{
                  width: "100%", minHeight: "44px", fontSize: "14px", fontWeight: 600,
                  borderRadius: "22px", border: "2px solid #FEE2E2", cursor: "pointer",
                  background: "white", color: "#991B1B",
                }}
              >
                🗑️ Retirer de mes crushs
              </button>
            )}
          </div>

          {/* ── Footer discret ── */}
          <div style={{ padding: "4px 4px 0", textAlign: "center" }}>
            <p style={{ fontSize: "11px", fontFamily: "monospace", color: "#cbd5e1", margin: "0 0 3px" }}>
              Code-barres : {product.barcode}
            </p>
            <p style={{ fontSize: "10px", color: "#e2e8f0", margin: 0 }}>
              Ajouté par la communauté
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default CommunityProductPage;
